import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getCourses, getEnrollments } from '../../utils/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { 
  BookOpen, Users, Award, GraduationCap, BarChart3, 
  Clock, Eye, TrendingUp, UserCheck, FileText, Plus, Edit2, HelpCircle, Inbox
} from 'lucide-react';
import { toast } from 'sonner';
import { ProfessorMetricsBlock } from './ProfessorMetricsBlock';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ProfessorDashboard = () => {
  const { user } = useAuth();
  const [courses, setCourses] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const coursesRes = await getCourses();
      const myCourses = (coursesRes.data || []).filter(c => c.instructor_id === user.id);
      setCourses(myCourses);

      // Get enrollments for all my courses
      const allEnrollments = [];
      for (const course of myCourses) {
        try {
          const res = await getEnrollments({ course_id: course.id });
          if (res.data) {
            allEnrollments.push(...res.data.map(e => ({ ...e, course_title: course.title })));
          }
        } catch (err) {
          console.error('Error fetching enrollments for course', course.id);
        }
      }
      setEnrollments(allEnrollments);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error al cargar los datos');
    } finally {
      setLoading(false);
    }
  };

  const totalStudents = enrollments.length;
  const activeStudents = enrollments.filter(e => e.status === 'active').length;
  const completedStudents = enrollments.filter(e => e.status === 'completed').length;
  const avgProgress = enrollments.length > 0
    ? enrollments.reduce((acc, e) => acc + (e.progress || 0), 0) / enrollments.length
    : 0;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent"></div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 bg-muted/30 min-h-screen" data-testid="professor-dashboard">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground mb-2">
            Panel del Profesor
          </h1>
          <p className="text-muted-foreground">Gestiona tus cursos y estudiantes, {user.name}</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-gradient-to-br from-indigo-50 to-indigo-100/50 border-indigo-200/50">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-indigo-500/10 rounded-lg">
                  <BookOpen className="h-5 w-5 text-indigo-600" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Mis Cursos</p>
                  <p className="text-2xl font-bold text-indigo-700">{courses.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100/50 border-emerald-200/50">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-500/10 rounded-lg">
                  <Users className="h-5 w-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Total Estudiantes</p>
                  <p className="text-2xl font-bold text-emerald-700">{totalStudents}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-amber-50 to-amber-100/50 border-amber-200/50">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-500/10 rounded-lg">
                  <GraduationCap className="h-5 w-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Completaron</p>
                  <p className="text-2xl font-bold text-amber-700">{completedStudents}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-rose-50 to-rose-100/50 border-rose-200/50">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-rose-500/10 rounded-lg">
                  <TrendingUp className="h-5 w-5 text-rose-600" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Progreso Prom.</p>
                  <p className="text-2xl font-bold text-rose-700">{avgProgress.toFixed(0)}%</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Métricas y desempeño económico */}
        <div className="mb-8">
          <ProfessorMetricsBlock />
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="md:col-span-2">
            <Tabs defaultValue="courses" className="w-full">
              <TabsList className="mb-4">
                <TabsTrigger value="courses">Mis Cursos</TabsTrigger>
                <TabsTrigger value="students">Estudiantes</TabsTrigger>
              </TabsList>

              <TabsContent value="courses">
                <Card>
                  <CardHeader className="pb-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-lg">Cursos Asignados</CardTitle>
                        <CardDescription>Gestiona el contenido de tus cursos</CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {courses.length === 0 ? (
                      <div className="text-center py-10">
                        <BookOpen className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                        <p className="text-muted-foreground">No tienes cursos asignados</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {courses.map((course) => {
                          const courseEnrollments = enrollments.filter(e => e.course_id === course.id);
                          const courseAvgProgress = courseEnrollments.length > 0
                            ? courseEnrollments.reduce((acc, e) => acc + (e.progress || 0), 0) / courseEnrollments.length
                            : 0;
                          
                          return (
                            <div 
                              key={course.id}
                              className="p-4 border rounded-xl hover:shadow-sm transition-all bg-background"
                              data-testid={`course-${course.id}`}
                            >
                              <div className="flex items-start justify-between gap-4">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-2">
                                    <h3 className="font-semibold text-foreground truncate">{course.title}</h3>
                                    <Badge variant={course.status === 'published' ? 'default' : 'secondary'}>
                                      {course.status}
                                    </Badge>
                                  </div>
                                  <div className="flex items-center gap-4 text-sm text-muted-foreground">
                                    <span className="flex items-center gap-1">
                                      <Users className="w-3.5 h-3.5" />
                                      {courseEnrollments.length} estudiantes
                                    </span>
                                    <span className="flex items-center gap-1">
                                      <BarChart3 className="w-3.5 h-3.5" />
                                      {courseAvgProgress.toFixed(0)}% promedio
                                    </span>
                                    <span className="flex items-center gap-1">
                                      <Clock className="w-3.5 h-3.5" />
                                      {course.hours || 0}h
                                    </span>
                                  </div>
                                </div>
                                <div className="flex gap-2">
                                  <Link to={`/courses/${course.id}`}>
                                    <Button size="sm" variant="outline" className="gap-1">
                                      <Eye className="w-3.5 h-3.5" />
                                      Ver
                                    </Button>
                                  </Link>
                                  <Link to={`/admin/courses/${course.id}/content`}>
                                    <Button size="sm" variant="outline" className="gap-1" data-testid={`edit-content-${course.id}`}>
                                      <Edit2 className="w-3.5 h-3.5" />
                                      Contenido
                                    </Button>
                                  </Link>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="students">
                <Card>
                  <CardHeader className="pb-4">
                    <CardTitle className="text-lg">Estudiantes Inscritos</CardTitle>
                    <CardDescription>Seguimiento del progreso de tus estudiantes</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {enrollments.length === 0 ? (
                      <div className="text-center py-10">
                        <Users className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                        <p className="text-muted-foreground">No hay estudiantes inscritos en tus cursos</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {enrollments.slice(0, 10).map((enrollment, idx) => (
                          <div 
                            key={enrollment.id || idx}
                            className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/30 transition-colors"
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-accent/10 flex items-center justify-center">
                                <UserCheck className="w-4 h-4 text-accent" />
                              </div>
                              <div>
                                <p className="font-medium text-sm">{enrollment.user_name || 'Estudiante'}</p>
                                <p className="text-xs text-muted-foreground">{enrollment.course_title}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <div className="w-24">
                                <Progress value={enrollment.progress || 0} className="h-2" />
                              </div>
                              <span className="text-xs font-medium text-muted-foreground w-10">
                                {(enrollment.progress || 0).toFixed(0)}%
                              </span>
                              <Badge variant={enrollment.status === 'completed' ? 'default' : 'secondary'} className="text-xs">
                                {enrollment.status === 'completed' ? 'Completado' : 'En progreso'}
                              </Badge>
                            </div>
                          </div>
                        ))}
                        {enrollments.length > 10 && (
                          <p className="text-center text-sm text-muted-foreground pt-2">
                            Y {enrollments.length - 10} estudiantes más...
                          </p>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Quick Stats */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">Resumen de Actividad</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Estudiantes activos</span>
                  <span className="font-semibold text-emerald-600">{activeStudents}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Completaron cursos</span>
                  <span className="font-semibold text-amber-600">{completedStudents}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Tasa de finalización</span>
                  <span className="font-semibold text-indigo-600">
                    {totalStudents > 0 ? ((completedStudents / totalStudents) * 100).toFixed(0) : 0}%
                  </span>
                </div>
              </CardContent>
            </Card>

            {/* Quick Actions */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">Acciones Rápidas</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {courses.length > 0 ? (
                  <Link to={`/admin/courses/${courses[0].id}/content`} className="block">
                    <Button variant="outline" className="w-full justify-start gap-2">
                      <Plus className="w-4 h-4" />
                      Gestionar Contenido
                    </Button>
                  </Link>
                ) : (
                  <Button variant="outline" className="w-full justify-start gap-2" disabled>
                    <Plus className="w-4 h-4" />
                    Crear Lección
                  </Button>
                )}
                <Button variant="outline" className="w-full justify-start gap-2" disabled>
                  <FileText className="w-4 h-4" />
                  Crear Evaluación
                </Button>
                <Link to="/courses" className="block">
                  <Button variant="outline" className="w-full justify-start gap-2">
                    <Eye className="w-4 h-4" />
                    Ver Catálogo
                  </Button>
                </Link>
                <Link to="/admin/articles" className="block">
                  <Button variant="outline" className="w-full justify-start gap-2" data-testid="professor-articles-btn">
                    <FileText className="w-4 h-4" />
                    Mis Artículos
                  </Button>
                </Link>
                <Link to="/admin/enrollment-requests" className="block">
                  <Button variant="outline" className="w-full justify-start gap-2" data-testid="professor-requests-btn">
                    <Inbox className="w-4 h-4" />
                    Solicitudes de mis cursos
                  </Button>
                </Link>
                <Link to="/admin/help" className="block">
                  <Button variant="outline" className="w-full justify-start gap-2" data-testid="professor-help-btn">
                    <HelpCircle className="w-4 h-4" />
                    Centro de Ayuda
                  </Button>
                </Link>
              </CardContent>
            </Card>

            {/* Recent Activity */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">Actividad Reciente</CardTitle>
              </CardHeader>
              <CardContent>
                {enrollments.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    Sin actividad reciente
                  </p>
                ) : (
                  <div className="space-y-3">
                    {enrollments.slice(0, 5).map((enrollment, idx) => (
                      <div key={enrollment.id || idx} className="flex items-start gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-accent mt-2"></div>
                        <div>
                          <p className="text-sm">
                            <span className="font-medium">{enrollment.user_name || 'Estudiante'}</span>
                            {' '}se inscribió en{' '}
                            <span className="font-medium">{enrollment.course_title}</span>
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfessorDashboard;
